import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VoicePlayer = ({ lesson, isPlaying, setIsPlaying, playbackSpeed, setPlaybackSpeed, onLessonComplete }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1.0);
  const [isMuted, setIsMuted] = useState(false);
  const speechSynthesisRef = useRef(null);
  const utteranceRef = useRef(null);

  // Convert duration string (MM:SS) to seconds
  const parseDuration = (durationStr) => {
    const [minutes, seconds] = durationStr?.split(':')?.map(Number) || [0, 0];
    return (minutes * 60) + seconds;
  };

  useEffect(() => {
    setDuration(parseDuration(lesson?.duration));
  }, [lesson]);

  useEffect(() => {
    // Initialize speech synthesis
    if ('speechSynthesis' in window) {
      speechSynthesisRef.current = window.speechSynthesis;
    }

    return () => {
      // Cleanup on unmount
      if (speechSynthesisRef?.current) {
        speechSynthesisRef?.current?.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (isPlaying && lesson?.content) {
      startSpeech();
    } else {
      pauseSpeech();
    }

    return () => {
      if (speechSynthesisRef?.current) {
        speechSynthesisRef?.current?.cancel();
      }
    };
  }, [isPlaying, lesson]);

  const startSpeech = () => {
    if (!speechSynthesisRef?.current || !lesson?.content) return;

    // Cancel any ongoing speech
    speechSynthesisRef?.current?.cancel();

    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(lesson?.content);
    utteranceRef.current = utterance;

    // Configure voice settings
    utterance.rate = playbackSpeed;
    utterance.volume = isMuted ? 0 : volume;
    utterance.pitch = 1.0;
    utterance.lang = 'en-US';

    // Try to use a professional-sounding voice
    const voices = speechSynthesisRef?.current?.getVoices();
    const preferredVoice = voices?.find(voice => 
      voice?.name?.includes('Google') || 
      voice?.name?.includes('Microsoft') ||
      voice?.name?.includes('Samantha') ||
      voice?.name?.includes('Daniel')
    );
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    // Event handlers
    utterance.onend = () => {
      setIsPlaying(false);
      setCurrentTime(duration);
      onLessonComplete?.();
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsPlaying(false);
    };

    // Start speaking
    speechSynthesisRef?.current?.speak(utterance);

    // Simulate progress
    const interval = setInterval(() => {
      setCurrentTime(prev => {
        if (prev >= duration) {
          clearInterval(interval);
          return duration;
        }
        return prev + 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  };

  const pauseSpeech = () => {
    if (speechSynthesisRef?.current) {
      speechSynthesisRef?.current?.cancel();
    }
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    setCurrentTime(0);
    setIsPlaying(true);
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (isPlaying) {
      // Restart with new speed
      setIsPlaying(false);
      setTimeout(() => setIsPlaying(true), 100);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e?.target?.value);
    setVolume(newVolume);
    if (newVolume > 0) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const speedOptions = [0.75, 1.0, 1.25, 1.5, 2.0];

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-2">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
          <Icon name="Volume2" size={24} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground mb-1">
            Professional Voice Narration
          </h3>
          <p className="text-sm text-muted-foreground">
            High-quality text-to-speech instruction
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Main Controls */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <Button
          variant="outline"
          size="icon"
          onClick={handleRestart}
          disabled={currentTime === 0}
        >
          <Icon name="RotateCcw" size={20} />
        </Button>

        <Button
          variant="default"
          size="lg"
          onClick={handlePlayPause}
          className="w-16 h-16"
        >
          <Icon name={isPlaying ? 'Pause' : 'Play'} size={28} />
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={toggleMute}
        >
          <Icon name={isMuted ? 'VolumeX' : 'Volume2'} size={20} />
        </Button>
      </div>

      {/* Advanced Controls */}
      <div className="space-y-4">
        {/* Playback Speed */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Playback Speed
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            {speedOptions?.map((speed) => (
              <button
                key={speed}
                onClick={() => handleSpeedChange(speed)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  playbackSpeed === speed
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {speed}x
              </button>
            ))}
          </div>
        </div>

        {/* Volume Control */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Volume: {Math.round(volume * 100)}%
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
          />
        </div>
      </div>

      {/* Browser Support Notice */}
      {!('speechSynthesis' in window) && (
        <div className="mt-4 bg-warning/10 border border-warning/30 rounded-lg p-3 flex items-start gap-2">
          <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
          <p className="text-xs text-warning-foreground">
            Your browser doesn't support speech synthesis. Please use a modern browser like Chrome, Firefox, or Safari.
          </p>
        </div>
      )}
    </div>
  );
};

export default VoicePlayer;